import { useMemo } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import {
  useGetExportJobs,
  useUpdateExportJob,
} from '@/service-library/hooks/export-jobs'
import { Document } from '@/types/documents'
import { ExportJob } from '@/types/export-jobs'
import { prettifyDate } from '@/utils/time'
import { Dialog, DialogContent } from '@/components/dialog'
import useZerapixTable from '../zerapix-table/useZerapixTable'
import ZerapixTable from '../zerapix-table/ZerapixTable'

type ExportJobsDialogProps = {
  overlay: OverlayState
  document: Document
}

export default function ExportJobsDialog({
  overlay,
  document,
}: ExportJobsDialogProps) {
  const { exportJobs = [] } = useGetExportJobs({
    filters: {
      limit: '1000',
      document_id: document.id,
      fields__include: 'created_at',
    },
    enabled: !!overlay.isOpen,
    refetchInterval: 2000,
  })

  const { updateExportJob } = useUpdateExportJob()

  function setExportJobToInvalid(exportJob: ExportJob) {
    updateExportJob({
      ...exportJob,
      state: 'invalid',
      status: 'Marked as invalid by a superuser.',
    })
  }

  const columns = useMemo<MRT_ColumnDef<ExportJob>[]>(() => {
    return [
      {
        header: 'ID',
        accessorKey: 'id',
        enableClickToCopy: true,
      },
      {
        header: 'State',
        accessorKey: 'state',
        Cell: ({ row }) => {
          let color = 'inherit'
          if (row.original.state === 'error') {
            color = 'red'
          } else if (row.original.state === 'completed') {
            color = 'green'
          }
          return (
            <span
              style={{
                color,
              }}
            >
              {row.original.state}
            </span>
          )
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
      },
      {
        header: 'Document ID',
        accessorKey: 'document_id',
        enableClickToCopy: true,
      },
      {
        header: 'Workflow State ID',
        accessorKey: 'workflow_state_id',
        enableClickToCopy: true,
      },
      {
        header: 'Created At',
        accessorKey: 'created_at',
        Cell: ({ row }) =>
          row.original.created_at ? prettifyDate(row.original.created_at) : '',
      },
    ]
  }, [])

  const table = useZerapixTable<ExportJob>({
    columns,
    data: exportJobs,
    muiTablePaperProps: { elevation: 0 },
    enableColumnResizing: true,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box>
        <Tooltip title="Mark as invalid">
          <IconButton
            color="error"
            onClick={(e) => {
              e.stopPropagation()
              setExportJobToInvalid(row.original)
            }}
            size="small"
          >
            <RemoveCircleOutline />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    muiTableBodyRowProps: ({ row, isDetailPanel }) =>
      isDetailPanel
        ? {}
        : {
            onClick: () => row.toggleExpanded(),
            sx: {
              cursor: 'pointer',
              minHeight: '40px',
            },
          },
    renderDetailPanel: ({ row }) => (
      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Status: {row.original.status}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Metadata:
        </Typography>
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 1,
            p: 2,
            overflow: 'auto',
            maxHeight: 400,
          }}
        >
          <Box
            component="pre"
            sx={{
              margin: 0,
              fontSize: '0.875rem',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              whiteSpace: 'pre-wrap',
              color: '#212529',
              backgroundColor: 'transparent',
            }}
          >
            {JSON.stringify(row.original.metadata, null, 2)}
          </Box>
        </Box>
      </Box>
    ),
  })

  return (
    <Dialog {...overlay} title="Export Jobs" maxWidth="xl">
      <DialogContent>
        <ZerapixTable<ExportJob> table={table} />
      </DialogContent>
    </Dialog>
  )
}
